/* eslint-env worker */
import * as tf from "@tensorflow/tfjs";
import SVM from "libsvm-js/asm";
import { matrix, sum } from "mathjs";
import { MultinomialNB } from "ml-naivebayes";
import {
  RandomForestClassifier,
  RandomForestRegression,
} from "ml-random-forest";
import { gzip } from "pako";
import {
  ALL_MODELS,
  DATA_TYPES,
  DELIMINATOR,
  EXTRA_HEADERS,
  PCA_ENCODE,
  TEST_SPLIT,
  WORKER_TYPES,
} from "../constants";
import { makeHistData, _makeHistData } from "../dashboard/Common/makeHistData";
import {
  addFilterDropRows,
  applyFiltersToDash,
  calcAutoFilters,
  filterColumns,
} from "../dashboard/DataCleaning/filters";
import { calcCustomTopicsData } from "../dashboard/NLP/textProcessor";
import { calcVariableImpact } from "../dashboard/VariableImportance/calcVariableImpact";
import { calcVariableImportance } from "../dashboard/VariableImportance/calcVariableImportance";
import { calcF1Score, convertToNumber } from "../helpers/DataHelpers";
import { formatNumber, toPercent } from "../helpers/FormatNumber";
import { getPearson } from "../helpers/pearsonCorrelation";
import { kNearestNeighbours } from "../MachineLearning/kNearestNeighbours";
import { LogisticRegression } from "../MachineLearning/LogisticRegression";
import { NaiveBayes } from "../MachineLearning/NaiveBayes";
import { NeuralNet } from "../MachineLearning/NeuralNet";
import { RandomForest } from "../MachineLearning/RandomForest";
import { RidgeRegression } from "../MachineLearning/RidgeRegression";
import { SupportVectorMachine } from "../MachineLearning/SupportVectorMachine";
import Column from "./Column";
import Dataset from "./Dataset";
import { LinearRegression } from "./LinearRegression";
import { PCAEncoder } from "./PCAReduction";
import { makePredictionsAsString } from "./predictor";

export const WEIGHTED = true;
export const MAX_WEIGHT = 3;
export const FAILED_STATUS = 0;
export const SUCCESS_STATUS = 1;

function handleError(inThis, error) {
  inThis.postMessage({ status: FAILED_STATUS, error: error.stack });
}
onmessage = function (e) {
  try {
    if (e.data.workerType === WORKER_TYPES.trainDataProcessor) {
      const yColName = e.data.yColName;
      const maxRows = e.data.maxRows;
      const dataset = Object.assign(
        new Dataset(undefined, undefined),
        JSON.parse(e.data.dataset)
      );

      let mlData = dataset.getMLData(yColName, undefined, maxRows);
      if (PCA_ENCODE) {
        const pcaEncoder = new PCAEncoder();
        pcaEncoder.train(mlData.X);
        mlData = pcaEncoder.encodeTrainSet(mlData);
      }
      let trainData = dataset.splitTrainData(mlData, TEST_SPLIT);
      this.postMessage({
        status: SUCCESS_STATUS,
        trainData: trainData,
      });
    } else if (e.data.workerType === WORKER_TYPES.trainer) {
      let trainData = e.data.trainData;
      const modelName = e.data.modelName;
      const dataInfo = e.data.dataInfo;
      let isCanceled = e.data.isCanceled;
      var state = undefined;
      if (modelName === ALL_MODELS.randomForest) {
        var modelClass = RandomForest;
        var converter = convertModelRF;
      } else if (modelName === ALL_MODELS.svm) {
        var modelClass = SupportVectorMachine;
        var converter = convertModelSVM;
      } else if (modelName === ALL_MODELS.ridgeReg) {
        var modelClass = RidgeRegression;
        var converter = convertModelRidgeReg;
      } else if (modelName === ALL_MODELS.logistic) {
        var modelClass = LogisticRegression;
        var converter = convertModelSVM;
      } else if (modelName === ALL_MODELS.linReg) {
        var modelClass = LinearRegression;
        var converter = convertModelLinearRegression;
      }
      if (modelClass != undefined) {
        if (WEIGHTED && dataInfo.outputType === DATA_TYPES.categorical) {
          dataInfo.weights = getLossWeights(trainData.yTrain);
        }

        let model = new modelClass(dataInfo);
        model.train(trainData, state, this.postMessage, isCanceled);
        let yHat = model.predict(trainData.xTest);
        let score = model.lowestLoss;
        if (model.mlDataInfo.outputType === DATA_TYPES.categorical) {
          score = calcF1Score(trainData.yTest, yHat);
        }

        this.postMessage({
          status: SUCCESS_STATUS,
          model: converter(model.model),
          lowestLoss: model.lowestLoss,
          trainState: "trained",
          yHat: yHat,
          yTest: trainData.yTest,
          score: score,
        });
      }
    } else if (e.data.workerType == WORKER_TYPES.postTrainProcess) {
      let trainData = e.data.trainData;
      let yCol = Object.assign(new Column(undefined, undefined), e.data.yCol);
      let allColumns = e.data.allColumns.map((col) =>
        Object.assign(new Column(undefined, undefined), col)
      );
      let convertedModels = e.data.models;

      let savedModels = [];
      for (const model of convertedModels) {
        savedModels.push(loadSavedModels(model)[0]);
      }

      let yHat = Dataset.deNormalize(
        savedModels[0].predict(trainData.xTest),
        yCol.colStats
      );
      let yTest = Dataset.deNormalize(trainData.yTest, yCol.colStats);
      let outVals = { yTest, yHat };

      let score = 0;
      if (yCol.columnType === DATA_TYPES.categorical) {
        let myMap = {};
        for (let i = 0; i < yHat.length; i++) {
          let predictedVal = yCol.getClassByIndex(yHat[i]);
          let testVal = yCol.getClassByIndex(yTest[i]);
          if (predictedVal in myMap) {
            myMap[predictedVal].predictedCount += 1;
          } else {
            myMap[predictedVal] = {
              actualCount: 0,
              predictedCount: 1,
              correct: 0,
              accuracy: 0,
            };
          }
          if (testVal in myMap) {
            myMap[testVal].actualCount += 1;
          } else {
            myMap[testVal] = {
              actualCount: 1,
              predictedCount: 0,
              correct: 0,
              accuracy: 0,
            };
          }
          if (predictedVal === testVal) {
            myMap[testVal].correct += 1;
          }
        }
        let keys = Array.from(Object.keys(myMap));
        for (let key of keys) {
          let actual = myMap[key].actualCount;
          if (actual === 0) {
            continue;
          }
          let correct = myMap[key].correct;
          myMap[key].accuracy = (100 * correct) / actual;
        }
        // state.setCatModelPredictions(myMap);
        score = calcF1Score(yTest, yHat);
        // state.setModelR2(f1);
        outVals = myMap;
      } else {
        score = getPearson(yTest, yHat);
        // state.setModelR2(r2);
      }

      const { out, importantVars } = calcVariableImportance(
        savedModels,
        trainData,
        allColumns
      );
      let importantCols = [];
      for (let col of allColumns) {
        if (importantVars.includes(col.columnName)) {
          importantCols.push(col);
        }
      }

      let varImpact = calcVariableImpact(
        savedModels,
        trainData,
        importantCols,
        yCol
      );

      this.postMessage({
        status: SUCCESS_STATUS,
        score: score,
        outVals: outVals,
        out: out,
        importantVars: importantVars,
        varImpact: varImpact,
      });
    } else if (e.data.workerType === WORKER_TYPES.processDroppedData) {
      let dataset2 = new Dataset(e.data.dataStr, e.data.shuffle);
      let filters = e.data.filters;
      // e.datafd.fdasf = 1;
      let rowsToDrop = e.data.rowsToDrop;
      let autoFilters = undefined;
      if (filters === undefined) {
        autoFilters = calcAutoFilters(dataset2);
        filters = [...autoFilters];
      }
      let { dataset, modified } = applyFiltersToDash(
        dataset2,
        filters,
        rowsToDrop
      );
      const cors = calcCorrelations(dataset.columns);

      let col = undefined;
      for (let colv of dataset.columns) {
        if (
          colv.columnType === DATA_TYPES.number ||
          colv.columnType === DATA_TYPES.categorical
        ) {
          col = colv;
          break;
        }
      }
      let dataJSON = JSON.parse(JSON.stringify(dataset));
      dataJSON.data = dataset.data;
      let histData = undefined;
      let histDataType = undefined;
      if (col !== undefined) {
        histData = makeHistData(
          dataset.data[col.columnName],
          col.columnType,
          rowsToDrop,
          col.colStats.median
        );
        histDataType = col.columnType;
      }
      this.postMessage({
        status: SUCCESS_STATUS,
        dataset: dataJSON,
        histData: histData,
        filters: filters,
        rowsModified: 0,
        rowsDropped: rowsToDrop,
        cors: cors,
        histDataType: histDataType,
        valuesModified: modified,
        autoFilters: autoFilters,
      });
    } else if (e.data.workerType === WORKER_TYPES.applyFiltersToDash) {
      let columns = e.data.columns;
      let data = e.data.data;
      let filters = e.data.filters;
      let rowsToDrop = e.data.rowsToDrop;
      let nlpColumns = e.data.nlpColumns;

      let filterData = filterColumns(columns, data, filters, rowsToDrop);
      let outCols = filterData.columns;
      let valuesModified = filterData.modified;
      let rawFrequencies = outCols.map((col) => col.rawFrequency);
      const cors = calcCorrelations(columns);

      let rowsToDropFull = addFilterDropRows(data, filters, {
        ...rowsToDrop,
      });

      this.postMessage({
        status: SUCCESS_STATUS,
        columns: outCols,
        cors: cors,
        rawFrequencies: JSON.stringify(rawFrequencies),
        valuesModified: valuesModified,
      });
    } else if (e.data.workerType == WORKER_TYPES.applyTopics) {
      let templateDataset = loadDataset(e.data.dataset);
      let dataset = new Dataset(
        undefined,
        true,
        templateDataset,
        e.data.customTopics,
        templateDataset.data
      );
      const out = applyFiltersToDash(
        dataset,
        e.data.filters,
        e.data.rowsToDrop
      );

      let dataJSON = JSON.parse(JSON.stringify(out.dataset));
      dataJSON.data = dataset.data;
      this.postMessage({
        status: SUCCESS_STATUS,
        datasetJSON: dataJSON,
      });
    } else if (e.data.workerType == WORKER_TYPES.batchPrediction) {
      const dataStr = e.data.dataStr;
      const convertedModels = e.data.convertedModels;
      const trainDataset = loadDataset(e.data.trainDataset);
      const valueToPredict = e.data.valueToPredict;
      let filters = e.data.filters;

      let savedModels = [];
      for (const model of convertedModels) {
        savedModels.push(loadSavedModels(model)[0]);
      }

      let dataset = new Dataset(
        dataStr,
        false,
        trainDataset,
        e.data.customTopics
      );
      const missingCols = validateTestData(
        dataset,
        trainDataset,
        valueToPredict
      );
      if (missingCols.length > 0) {
        this.postMessage({
          status: SUCCESS_STATUS,
          data: [1, 23],
          isValidated: false,
          missingColumns: missingCols.map((col) => col.columnName),
        });
        return;
      }

      filters = filters.filter((f) => !f.drop_row);
      let outData = applyFiltersToDash(dataset, filters, new Set());
      dataset = outData.dataset;

      let mlData = dataset.getMLData(
        valueToPredict,
        trainDataset.clolumnsToDrop,
        undefined,
        true
      );
      const outputStr = makePredictionsAsString(
        mlData.X,
        trainDataset,
        valueToPredict,
        savedModels
      );

      // Set table data
      // Set Header
      let headers = [
        EXTRA_HEADERS.row_number,
        EXTRA_HEADERS.predited.replace("{val}", valueToPredict),
      ];
      const colType = trainDataset.getColumn(valueToPredict).columnType;
      const isCat = colType === DATA_TYPES.categorical;
      if (isCat) {
        headers.push(EXTRA_HEADERS.probabilites);
      }
      for (let col of dataset.columns) {
        headers.push(col.columnName);
      }

      let data = [];
      for (let i = 0; i < outputStr.length; i++) {
        data.push([i + 1]);
      }

      for (let i = 0; i < outputStr.length; i++) {
        if (isCat) {
          let vals = outputStr[i].split(DELIMINATOR);
          data[i].push(vals[0]);
          data[i].push(
            convertToNumber(formatNumber(100 * convertToNumber(vals[1])))
          );
        } else {
          data[i].push(convertToNumber(outputStr[i]));
        }

        for (let j = isCat ? 3 : 2; j < headers.length; j++) {
          let val = dataset.data[headers[j]][i];

          if (dataset.getColumn(headers[j]).columnType === DATA_TYPES.number) {
            val = convertToNumber(val);
          }

          data[i].push(val);
        }
      }

      // Histogram Data
      let dataCol = [];
      let ndx = isCat ? 2 : 1;
      for (let i = 0; i < data.length; i++) {
        dataCol.push(convertToNumber(data[i][ndx]));
      }
      let histData = _makeHistData(dataCol);

      this.postMessage({
        status: SUCCESS_STATUS,
        data: data,
        headers: headers,
        histData: histData,
        isValidated: true,
        missingColumns: missingCols,
      });
    } else if (e.data.workerType === WORKER_TYPES.customTopics) {
      let sentences = e.data.sentences;
      let topics = e.data.topics;
      let cloudData = e.data.cloudData;
      let prevPlotPoints = e.data.prevPlotPoints;
      let docs = e.data.docs;
      let out = calcCustomTopicsData(
        sentences,
        topics,
        cloudData,
        prevPlotPoints,
        docs
      );
      this.postMessage({
        status: SUCCESS_STATUS,
        customTopicsData: out,
      });
    } else if (e.data.workerType === WORKER_TYPES.gzip) {
      const data = e.data.data;
      const gzipped = gzip(data);
      this.postMessage({
        status: SUCCESS_STATUS,
        gzipped: gzipped,
      });
    }
  } catch (error) {
    handleError(this, error);
  }
};

export function convertModelRF(model) {
  return JSON.stringify(model);
}
export function convertModelSVM(model) {
  return model.serializeModel();
}
export function convertModelRidgeReg(model) {
  return model;
}
export function convertModelLinearRegression(model) {
  return model;
}
function makeJSONData(data, yColName, output) {
  let colNames = Object.keys(data);
  let colNamesOrig = [...colNames];
  colNames.push("Predicted: " + yColName);
  let toSplit = output[0].includes(DELIMINATOR);
  if (toSplit) {
    colNames.push("Probability");
  }
  const nRows = data[colNames[0]].length;
  let outData = [];
  for (let i = 0; i < nRows; i++) {
    let tmp = {};
    let count = 0;
    for (let colName of colNamesOrig) {
      tmp[count] = data[colName][i];
      count += 1;
    }
    let pred = output[i];
    if (toSplit) {
      let preds = pred.split(DELIMINATOR);
      tmp["Predicted: " + yColName] = preds[0];
      tmp["Probability"] = toPercent(preds[1]);
    } else {
      tmp["Predicted: " + yColName] = pred;
    }
    outData.push(tmp);
  }
  return [outData, colNames];
}

function makeCSVData(data, yColName, output) {
  let keys = Object.keys(data);

  let nRows = data[keys[0]].length;
  let rows = [];
  for (let i = 0; i < nRows; i++) {
    let row = "";
    for (let key of keys) {
      row += data[key][i];
    }
    rows.push(row);
  }
  let outStr = rows[0] + ", Predicted: " + yColName;
  let toSplit = output[0].includes(DELIMINATOR);
  if (toSplit) {
    outStr += ",Probability\r\n";
  } else {
    outStr += "\r\n";
  }
  for (let i = 1; i < rows.length; i++) {
    let val = output[i - 1];
    if (toSplit) {
      let splitVal = val.split(DELIMINATOR);
      val = splitVal[0] + "," + toPercent(splitVal[1]);
    }
    outStr += rows[i] + "," + val + "\r\n";
  }
  return outStr;
}

export function loadDataset(value) {
  let parsed = JSON.parse(value);
  let tmp = Object.assign(new Dataset(undefined, undefined), parsed);
  tmp.columns = tmp.columns.map((col) =>
    Object.assign(new Column(undefined, undefined), col)
  );
  return tmp;
}

function validateTestData(dataset, trainDataset, valueToPredict) {
  const colNames = new Set(dataset.columns.map((col) => col.columnName));
  const missingColumns = trainDataset.columns.filter(
    (col) =>
      !colNames.has(col.columnName) &&
      col.columnName !== valueToPredict &&
      col.colType !== DATA_TYPES.unusable
  );
  return missingColumns;
}

//@param models - {'modelName': value}
export function loadSavedModels(models) {
  // Load saved models
  let savedModels = [];
  for (const [modelName, value] of Object.entries(models)) {
    switch (modelName) {
      case ALL_MODELS.linReg:
        savedModels.push(loadLinearRegression(value, LinearRegression));
        break;
      case ALL_MODELS.ridgeReg:
        savedModels.push(loadLinearRegression(value, RidgeRegression));
        break;
      case ALL_MODELS.logistic:
        savedModels.push(loadLogistic(value));
        break;
      case ALL_MODELS.bayes:
        savedModels.push(loadBayes(value));
        break;
      case ALL_MODELS.knn:
        savedModels.push(loadKNN(value));
        break;
      case ALL_MODELS.randomForest:
        savedModels.push(loadRF(value));
        break;
      case ALL_MODELS.svm:
        savedModels.push(loadSVM(value));
        break;
      case ALL_MODELS.neuralNet:
        savedModels.push(loadTensor(value));
    }
  }
  return savedModels;
}

function loadKNN(value) {
  let parsed = JSON.parse(value);
  let model = Object.assign(new kNearestNeighbours(parsed.mlDataInfo), parsed);
  return model;
}
function loadSVM(value) {
  let parsed = JSON.parse(value);
  const svm = new SupportVectorMachine(parsed.mlDataInfo);
  svm.model = SVM.load(parsed.model);
  svm.lowestLoss = parsed.lowestLoss;
  return svm;
}
function loadLogistic(value) {
  let parsed = JSON.parse(value);
  const svm = new LogisticRegression(parsed.mlDataInfo);
  svm.lowestLoss = parsed.lowestLoss;
  svm.model = SVM.load(parsed.model);
  return svm;
}
function loadBayes(value) {
  let parsed = JSON.parse(value);
  let model = Object.assign(new NaiveBayes(parsed.mlDataInfo), parsed);
  model.model = MultinomialNB.load(model.model);
  return model;
}

function loadRF(value) {
  let parsed = JSON.parse(value);
  let rf = new RandomForest(parsed.mlDataInfo);
  let modelType =
    parsed.mlDataInfo.outputType === DATA_TYPES.categorical
      ? RandomForestClassifier
      : RandomForestRegression;
  rf.model = modelType.load(parsed.model);
  rf.lowestLoss = parsed.lowestLoss;
  return rf;
}

function loadLinearRegression(value, modelType) {
  let parsed = JSON.parse(value);
  let model = Object.assign(new modelType(parsed.mlDataInfo), parsed);
  let coeff = {
    _data: model.model.coeff.data,
    _size: model.model.coeff.size,
  };
  model.model.coeff = Object.assign(matrix(), coeff);

  return model;
}

function loadTensor(value) {
  let parsed = JSON.parse(value);
  let model = new NeuralNet(parsed.mlDataInfo);
  let tensors = parsed.bestModelWeights.map((val) =>
    tf.tensor(JSON.parse(val))
  );
  model.bestModelWeights = tensors;
  model.model.setWeights(tensors);
  model.lowestLoss = parsed.lowestLoss;
  return model;
}

export function calcCorrelations(columns) {
  let colNames = [];

  for (let col of columns) {
    if (col.columnType !== DATA_TYPES.unusable) {
      colNames.push(col.columnName);
    }
  }
  colNames = colNames.sort((a, b) =>
    a.toLowerCase() < b.toLowerCase() ? -1 : 1
  );

  const correlations = new Array(colNames.length)
    .fill(0)
    .map(() => new Array(colNames.length).fill(0));

  let datasetColOrders = columns.map((col) => col.columnName);
  for (let i = 0; i < colNames.length; i++) {
    let ndxI = datasetColOrders.indexOf(colNames[i]);
    const colI = columns[ndxI].vectorizedData;
    for (let j = 0; j < colNames.length; j++) {
      let ndxJ = datasetColOrders.indexOf(colNames[j]);
      const colJ = columns[ndxJ].vectorizedData;

      if (
        columns[ndxI].availableColumnTypes.includes(DATA_TYPES.number) &&
        columns[ndxJ].availableColumnTypes.includes(DATA_TYPES.number)
      ) {
        if (i === j) {
          correlations[i][j] = 1;
        } else if (i > j) {
          correlations[i][j] = correlations[j][i];
        } else {
          correlations[i][j] = getPearson(colI, colJ);
        }
      } else {
        correlations[i][j] = 0;
      }
    }
  }
  const tmp = { correlations: correlations, colNames: colNames };
  return tmp;
}

function makeNLPDataWithFilters(data, rowsToDrop, nlpColumns) {
  let dataCopy = data;
  for (let key of Object.keys(dataCopy)) {
    let ar = [...dataCopy[key]].filter((w, i) => !rowsToDrop.has(i));
    dataCopy[key] = ar;
  }

  let nlpData = Dataset.makeNLPData(dataCopy, nlpColumns);
  return nlpData;
}

export function getLossWeights(yTrain) {
  let counts = new Map();
  for (let val of yTrain) {
    if (counts.has(val)) {
      counts.set(val, counts.get(val) + 1);
    } else {
      counts.set(val, 1);
    }
  }
  let keys = Array.from(counts.keys()).sort();
  let countsAr = [];
  for (let key of keys) {
    countsAr.push(counts.get(key));
  }
  let total = sum(countsAr);
  // return undefined;

  for (let i = 0; i < countsAr.length; i++) {
    let val = countsAr[i];
    if (val === 0) {
      countsAr[i] = 0;
    } else {
      countsAr[i] = Math.min(total / val, MAX_WEIGHT);
    }
  }
  return countsAr;
}
